import { Actions, Button, Form, Image, Modal, Text, Tray } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { useTranslation } from 'react-i18next';
import useDealAlert from '../../../lib/hooks/useDealAlert';
import { formatLocaleTime, formatTimestamp } from '../../../lib/utils/formatDateTime';
import CountdownClock from '../CountdownClock';
import ZoomSlider from '../ZoomSlider';
import styles from './DealAlertStatus.module.css';
import StatusPrimaryText from './StatusPrimaryText';

const cx = classnames.bind(styles);

const megabytes = n => n * 1000000;

function canvasToBlob(canvas, options = {}) {
  const { mimeType, quality } = options;

  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(resolve, mimeType, quality);
    } catch (error) {
      reject(error);
    }
  });
}

async function compress(canvas, maxSize = 1, mimeType) {
  const maxBytes = megabytes(maxSize);
  let quality = 1;
  let blob = await canvasToBlob(canvas, { mimeType, quality });

  while ((blob.size > maxBytes) && (quality > 0.1)) {
    quality -= 0.05;
    blob = canvasToBlob(canvas, { quality });
  }

  return blob;
}

function DealAlertStatus({ className, isPwGBMember }) {
  const { t } = useTranslation(['feature', 'miscellaneous']);
  const {
    dealAlert,
    isUploading: uploading,
    uploadReceipt,
    didUploadReceipt: uploaded,
  } = useDealAlert();
  const expires = dealAlert?.transactBy;
  const uploadReceiptExpiry = dealAlert?.submitBy;
  const fileInput = useRef();
  const receiptForm = useRef();
  const editor = useRef();
  const [receipt, setReceipt] = useState();
  const [userSaidNo, setUserSaidNo] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [didExpire, setDidExpire] = useState(expires && (new Date(expires) < new Date()));

  // Update messaging if expiry passes while user is on the page
  const handleExpiration = useCallback(() => {
    setDidExpire(true);
  }, []);

  const sayNo = useCallback(() => {
    setUserSaidNo(true);
  }, []);

  const handleUploadClick = useCallback(() => {
    if (fileInput.current) {
      fileInput.current.click();
    }
  }, []);

  const handleReceipt = useCallback(({ target }) => {
    const { files: [file] } = target;

    // Only open the modal if a file was selected
    if (file) {
      setReceipt(file);
    }
  }, []);

  const handleScale = useCallback((e) => {
    setScale(parseFloat(e.target.value));
  }, []);

  // Mark flash deal as expired appropriately if flash deal data changes
  useEffect(() => {
    setDidExpire(expires && (new Date(expires) < new Date()));
  }, [expires]);

  const questions = useMemo(() => {
    const list = [];

    if (!dealAlert) {
      return [];
    }

    const { claimedAt, transactBy } = dealAlert;
    const startTime = formatLocaleTime(claimedAt);
    const endTime = formatLocaleTime(transactBy);

    list.push(t('dealAlertQuestionPayment', { ns: 'feature' }));
    list.push(t('dealAlertQuestionCardInfo', { ns: 'feature' }));
    list.push(t('dealAlertQuestionAddress', { ns: 'feature' }));
    list.push(t('dealAlertQuestionDateTime', { ns: 'feature' }));
    list.push(t('dealAlertQuestionTimeRange', { ns: 'feature', startTime, endTime }));
    list.push(t('dealAlertQuestionFuelType', { ns: 'feature' }));
    list.push(t('dealAlertQuestionGallons', { ns: 'feature' }));
    list.push(t('dealAlertQuestionAmount', { ns: 'feature' }));

    return list;
  }, [dealAlert, t]);

  const submitReceipt = useCallback(() => {
    const MAX_SIZE = 0.5;

    if (receipt.size < (megabytes(MAX_SIZE))) {
      uploadReceipt({
        variables: {
          offerId: dealAlert?.offerId,
          receipt,
        },
      });
    } else {
      compress(editor.current.getImage(), MAX_SIZE, receipt.type)
        .then((compressedReceipt) => {
          uploadReceipt({
            variables: {
              offerId: dealAlert?.offerId,
              receipt: new File([compressedReceipt], receipt.name, { type: compressedReceipt.type }),
            },
          });
        });
    }
  }, [dealAlert, receipt, uploadReceipt]);

  const resetReceiptForm = useCallback(() => {
    setReceipt(undefined);
    setUserSaidNo(false);
    setQuestionIndex(0);
    receiptForm.current.reset();
  }, []);

  const handleModalClose = useCallback(() => {
    resetReceiptForm();
  }, [resetReceiptForm]);

  const progressQuestion = useCallback(() => {
    const nextIndex = questionIndex + 1;

    if (questionIndex === questions.length - 1) {
      submitReceipt();
    } else {
      setQuestionIndex(nextIndex);
    }
  }, [questions.length, questionIndex, submitReceipt]);

  const handleDifferentReceipt = useCallback((e) => {
    resetReceiptForm();
    handleUploadClick(e);
  }, [handleUploadClick, resetReceiptForm]);

  const getModalContent = useCallback(({ close }) => {
    if (uploaded) {
      return (
        <div>
          <Text as="p" bold fluid size="lg">
            {t('dealAlertUploadSuccess', { ns: 'feature' })}
          </Text>
          <Image
            alt="Check Circle"
            src="//static.gasbuddy.com/web/consumer/vehicles/Checkmark.svg"
            className={cx('checkCircle')}
          />
          <Text as="p">
            {t('gasbackOnTheWay', { ns: 'feature' })}
          </Text>
          <br />
          <Button onClick={close} primary wide>
            {t('actions.done', { ns: 'miscellaneous' })}
          </Button>
        </div>
      );
    }

    if (userSaidNo) {
      if (questionIndex === 0) {
        return (
          <div>
            <Text as="p" bold fluid size="lg">
              {t('unableToClaim', { ns: 'feature' })}
            </Text>
            <Text as="p">
              {t('onlyValidForCards', { ns: 'feature' })}
            </Text>
            <Text as="p">
              {t('readDealDetails', { ns: 'feature' })}
            </Text>
            <br />
            <Button onClick={close} primary wide>
              {t('actions.close', { ns: 'miscellaneous' })}
            </Button>
            <Button link onClick={handleDifferentReceipt}>
              {t('useDifferentReceipt', { ns: 'feature' })}
            </Button>
          </div>
        );
      }

      return (
        <div>
          <Text as="p" bold fluid size="lg">
            {t('somethingsMissing', { ns: 'feature' })}
          </Text>
          <Text as="p">
            {t('canStillReview', { ns: 'feature' })}
          </Text>
          <br />
          <Button onClick={submitReceipt} primary wide>
            {t('uploadReceiptNow', { ns: 'feature' })}
          </Button>
          <Button link onClick={handleDifferentReceipt}>
            {t('useDifferentReceipt', { ns: 'feature' })}
          </Button>
        </div>
      );
    }

    return (
      <div style={{ textAlign: 'center' }}>
        <AvatarEditor
          image={receipt}
          height={300}
          ref={editor}
          rotate={0}
          scale={scale}
          width={150}
        />
        <ZoomSlider onChange={handleScale} />
        <br />
        <div>
          {questions.map((q, i) => (
            <div key={q} className={cx('step', { done: i <= questionIndex })} />
          ))}
        </div>
        <Text fluid>{questions[questionIndex]}</Text>
        <br />
        <Button loading={uploading} onClick={progressQuestion} primary wide>
          {t('actions.yes', { ns: 'miscellaneous' })}
        </Button>
        <Button onClick={sayNo} secondary wide>
          {t('actions.no', { ns: 'miscellaneous' })}
        </Button>
      </div>
    );
  }, [
    handleDifferentReceipt,
    handleScale,
    progressQuestion,
    questionIndex,
    questions,
    receipt,
    sayNo,
    scale,
    submitReceipt,
    uploaded,
    uploading,
    userSaidNo,
    t,
  ]);

  const uploadReceiptExpiryTimestamp = useMemo(() => uploadReceiptExpiry && formatTimestamp(uploadReceiptExpiry), [uploadReceiptExpiry]);

  if (!dealAlert) {
    return null;
  }

  return (
    <Tray className={cx('tray')} isOpen mobile={false}>
      <div className={cx('dealStatus', className)}>
        <Modal
          content={getModalContent}
          forceIsShowing={!!receipt}
          onClose={handleModalClose}
          size="sm"
        />
        <CountdownClock endDate={expires} onEnd={handleExpiration} />
        <StatusPrimaryText
          dealAlertStation={dealAlert?.station}
          didExpire={didExpire}
          isPwGBMember={isPwGBMember}
          uploadReceiptExpiryTimestamp={uploadReceiptExpiryTimestamp}
        />
        {uploadReceiptExpiry && (
          <Fragment>
            <Form ref={receiptForm}>
              <input
                accept="image/png, image/jpeg"
                onChange={handleReceipt}
                ref={fileInput}
                style={{ display: 'none' }}
                type="file"
              />
            </Form>
            <Actions.Button
              className={cx({ deal: !isPwGBMember })}
              desktop={6}
              onClick={handleUploadClick}
              secondary={isPwGBMember}
            >
              {isPwGBMember
                ? t('iDidntUseMyCard', { ns: 'feature' })
                : t('uploadReceipt', { ns: 'feature' })}
            </Actions.Button>
          </Fragment>
        )}
      </div>
    </Tray>
  );
}

DealAlertStatus.propTypes = {
  className: PropTypes.string,
  isPwGBMember: PropTypes.bool,
};

DealAlertStatus.defaultProps = {
  className: undefined,
  isPwGBMember: false,
};

export default DealAlertStatus;
